import { useContext, useEffect, useState } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";

import MUIPrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import MUIWarningAmberIcon from "@mui/icons-material/WarningAmber";
import MUIAccessTimeIcon from "@mui/icons-material/AccessTime";
import MUIPersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MUIErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import MUICheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import MUIExpandMoreIcon from "@mui/icons-material/ExpandMore";

import MUITypography from "@mui/material/Typography";
import MUIStack from "@mui/material/Stack";
import MUIButton from "@mui/material/Button";
import MUICircularProgress from "@mui/material/CircularProgress";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITextField from "@mui/material/TextField";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIAlert from "@mui/material/Alert";
import MUICollapse from "@mui/material/Collapse";

import Card from "../../Card";
import Term from "../../../Utility/Crud/Term";
import User from "../../../Utility/Crud/User";
import EmptyState from "../../EmptyState";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import api from "../../../Seating/Security/api";
import moment from "moment";
import * as Sentry from "@sentry/browser";
import DialogFunctional from "../../DialogFunctional";
import DialogConfirm from "../../Dialog/DialogConfirm";

/**
 * CardChartInsuranceIQ renders a Card component
 * displaying insurance iq rules that have been ran on the order.
 * @returns {JSX.Element}
 */
const className = "CardChartInsuranceIQ";
const title = "Insurance IQ";
const icon = MUIPrivacyTipIcon;
const feedbackTypeAllRulesAccurate = "all_rules_accurate";
const feedbackTypeSomeRulesInaccurate = "some_rules_inaccurate";
const feedbackTypeNeedNewRule = "need_new_rule";
const insuranceIQFeedbackMessage = {
  [feedbackTypeAllRulesAccurate]: "Insurance IQ rules marked accurate",
  [feedbackTypeSomeRulesInaccurate]: "Insurance IQ rules marked inaccurate",
  [feedbackTypeNeedNewRule]: "Insurance IQ new rule requested",
};
const insuranceIQFeedbackConfirmText = {
  [feedbackTypeAllRulesAccurate]:
    "All successful and failed rules are accurate and do not require any changes",
  [feedbackTypeSomeRulesInaccurate]:
    "One or more of the rules is inaccurate and needs changes",
  [feedbackTypeNeedNewRule]:
    "Something was wrong that a rule does not exist for",
};

export default function CardChartInsuranceIQ(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);

  const insuranceIQRuleResponse = Object.values(
    chartContext.chart?.insuranceResponse,
  ).find(
    (responses) =>
      responses.orderActivityId === chartContext?.orderId &&
      responses.insuranceResponseTypeTermId === 232,
  );

  const insuranceIQRuleJsonResult = JSON.parse(
    insuranceIQRuleResponse?.jsonAnalysis ?? "{}",
  );
  const insuranceIQRules = insuranceIQRuleJsonResult?.data?.results ?? [];

  const failedOrWarningInsuranceIQRules = insuranceIQRules.filter(
    (rules) => rules.status === "fail" || rules.status === "warn",
  );
  const passedInsuranceIQRules = insuranceIQRules.filter(
    (rules) => rules.status === "pass",
  );
  const allInsuranceIQRulesPassed =
    failedOrWarningInsuranceIQRules.length === 0 &&
    passedInsuranceIQRules.length > 0;

  const [disableInsuranceIQButton, setDisabledInsuranceIQButton] =
    useState(false);
  const [successfulRulesExpanded, setSuccessfulRulesExpanded] = useState(false);
  const [insuranceIQFeedbackDialogOpen, setInsuranceIQFeedbackDialogOpen] =
    useState(false);
  const [insuranceIQFeedbackDialogType, setInsuranceIQFeedbackDialogType] =
    useState(null);
  const [pendingInsuranceIQFeedback, setPendingInsuranceIQFeedback] =
    useState(null);
  const [selectedInaccurateRules, setSelectedInaccurateRules] = useState([]);
  const [inaccurateRulesExplanation, setInaccurateRulesExplanation] =
    useState("");
  const [newRuleExplanation, setNewRuleExplanation] = useState("");
  const [
    showInsuranceIQFeedbackValidation,
    setShowInsuranceIQFeedbackValidation,
  ] = useState(false);

  useEffect(() => {
    setSuccessfulRulesExpanded(false);
  }, [insuranceIQRuleResponse?.jsonAnalysis]);

  const getInsuranceIQRuleDisplayName = (rule) => {
    return (
      Term.getDisplayName(
        mainContext.termsIndexed[rule["insuranceRuleTypeTermId"]],
      ) || `Rule ${rule["insuranceRuleTypeTermId"]}`
    );
  };

  const getInsuranceIQRuleSentryPayload = (rules) => {
    return rules.map((rule) => ({
      insuranceRuleTypeTermId: rule["insuranceRuleTypeTermId"],
      name: getInsuranceIQRuleDisplayName(rule),
      pass: rule["pass"],
      message: rule["message"] ?? "",
    }));
  };

  const captureInsuranceIQRulesFeedback = (
    feedbackType,
    inaccurateRules = [],
    explanation = null,
  ) => {
    const submittedAt = new Date().toISOString();
    const rules = getInsuranceIQRuleSentryPayload(insuranceIQRules);
    const selectedRules = getInsuranceIQRuleSentryPayload(inaccurateRules);
    let selectedRulesNames = [];
    let selectedRulesMessage = [];

    if (selectedRules.length > 0) {
      selectedRules.forEach((element) => {
        selectedRulesNames.push(element.name);
        selectedRulesMessage.push(element.message);
      });
    }

    Sentry.withScope((scope) => {
      scope.setTag("insurance_iq_feedback", feedbackType);
      scope.setTag(
        "insurance_iq_order_id",
        chartContext.orderId != null ? String(chartContext.orderId) : "none",
      );
      scope.setTag("explanation", explanation ?? "none");
      scope.setTag("submitted_at", submittedAt);
      scope.setTag(
        "submitted_by_userprofile_id",
        mainContext.currentUser?.id ?? "none",
      );
      scope.setTag(
        "submitted_by_username",
        mainContext.currentUser?.username ?? "none",
      );
      scope.setTag("user_role", mainContext.currentUser?.role ?? "none");
      scope.setTag("patient_id", chartContext.patientId ?? "none");
      scope.setTag("order_activity_id", chartContext.orderId ?? "none");
      scope.setTag(
        "insurance_response_id",
        insuranceIQRuleResponse?.id ?? "none",
      );
      scope.setTag(
        "insurance_response_timestamp",
        insuranceIQRuleResponse?.timestamp ?? "none",
      );
      scope.setTag("rule_count", rules.length);
      scope.setTag("inaccurate_rule_count", selectedRules.length);
      scope.setTag(
        "inaccurate_rule_names",
        selectedRulesNames?.join(",") ?? "none",
      );
      scope.setTag(
        "inaccurate_rule_messages",
        selectedRulesMessage?.join(",") ?? "none",
      );

      scope.setExtra("insuranceIQRules", rules);
      scope.setExtra("selectedInaccurateRules", selectedRules);

      Sentry.captureMessage(insuranceIQFeedbackMessage[feedbackType], {
        fingerprint: ["insuranceIQRulesFeedback", feedbackType],
        level: "info",
      });
    });
  };

  const queueAllRulesAccurateConfirmation = () => {
    setPendingInsuranceIQFeedback({
      inaccurateRules: [],
      explanation: null,
      feedbackType: feedbackTypeAllRulesAccurate,
    });
  };

  const handleConfirmInsuranceIQFeedback = () => {
    captureInsuranceIQRulesFeedback(
      pendingInsuranceIQFeedback.feedbackType,
      pendingInsuranceIQFeedback.inaccurateRules,
      pendingInsuranceIQFeedback.explanation,
    );
    NotistackEnqueueSnackbar("Insurance IQ feedback logged.", {
      variant: "success",
    });
    setPendingInsuranceIQFeedback(null);

    if (insuranceIQFeedbackDialogOpen) {
      handleCloseInsuranceIQFeedbackDialog();
    }
  };

  const handleAllRulesAccurate = () => {
    queueAllRulesAccurateConfirmation();
  };

  const handleCloseInsuranceIQFeedbackDialog = () => {
    setInsuranceIQFeedbackDialogOpen(false);
    setInsuranceIQFeedbackDialogType(null);
    setSelectedInaccurateRules([]);
    setInaccurateRulesExplanation("");
    setNewRuleExplanation("");
    setShowInsuranceIQFeedbackValidation(false);
  };

  const handleSomeRulesInaccurate = () => {
    setInsuranceIQFeedbackDialogType(feedbackTypeSomeRulesInaccurate);
    setInsuranceIQFeedbackDialogOpen(true);
    setShowInsuranceIQFeedbackValidation(false);
  };

  const handleNeedNewRule = () => {
    setInsuranceIQFeedbackDialogType(feedbackTypeNeedNewRule);
    setInsuranceIQFeedbackDialogOpen(true);
    setShowInsuranceIQFeedbackValidation(false);
  };

  const handleSubmitInsuranceIQFeedback = () => {
    const trimmedExplanation = inaccurateRulesExplanation.trim();
    const trimmedNewRuleExplanation = newRuleExplanation.trim();

    if (
      (insuranceIQFeedbackDialogType === feedbackTypeSomeRulesInaccurate &&
        (selectedInaccurateRules.length === 0 ||
          trimmedExplanation.length === 0)) ||
      (insuranceIQFeedbackDialogType === feedbackTypeNeedNewRule &&
        trimmedNewRuleExplanation.length === 0)
    ) {
      setShowInsuranceIQFeedbackValidation(true);
      return;
    }

    captureInsuranceIQRulesFeedback(
      insuranceIQFeedbackDialogType,
      insuranceIQFeedbackDialogType === feedbackTypeSomeRulesInaccurate
        ? selectedInaccurateRules
        : [],
      insuranceIQFeedbackDialogType === feedbackTypeSomeRulesInaccurate
        ? trimmedExplanation
        : trimmedNewRuleExplanation,
    );
    NotistackEnqueueSnackbar("Insurance IQ feedback logged.", {
      variant: "success",
    });
    handleCloseInsuranceIQFeedbackDialog();
  };

  /**
   * Disables button while sending a new Insurance IQ Request
   * Notification pops on success or failure
   * Reload chart data on success
   * @returns {Object}
   */
  const handleInsuranceIQRun = async () => {
    setDisabledInsuranceIQButton(true);
    return await api
      .send("POST", "/insuranceIQ", {
        insuranceIqTypeTermId: 231,
        orderActivityId: chartContext.orderId,
      })
      .then(() => {
        NotistackEnqueueSnackbar("Insurance IQ successfully ran on order.", {
          variant: "success",
        });
        setDisabledInsuranceIQButton(false);
        chartContext.reloadChartData();
      })
      .catch((error) => {
        console.error(error);
        NotistackEnqueueSnackbar("There was an error running Insurance IQ.", {
          variant: "error",
        });
        setDisabledInsuranceIQButton(false);
      });
  };

  /**
   * Renders InsuranceIQ Run Button
   * @returns {JSX.Element}
   */
  const renderInsuranceIQRunButton = () => {
    return (
      <MUIButton
        variant="contained"
        onClick={handleInsuranceIQRun}
        disabled={disableInsuranceIQButton}
        startIcon={
          disableInsuranceIQButton ? (
            <MUICircularProgress size={18} color="inherit" />
          ) : (
            <MUIPrivacyTipIcon />
          )
        }
        size="small"
      >
        Run Insurance IQ
      </MUIButton>
    );
  };

  const getActions = () => {
    return [
      <MUIButton
        variant="outlined"
        color="success"
        onClick={handleAllRulesAccurate}
        disabled={insuranceIQRules.length === 0}
        size="small"
      >
        All Accurate
      </MUIButton>,
      <MUIButton
        variant="outlined"
        color="warning"
        onClick={handleSomeRulesInaccurate}
        disabled={insuranceIQRules.length === 0}
        size="small"
      >
        Some Inaccurate
      </MUIButton>,
      <MUIButton
        variant="outlined"
        color="info"
        onClick={handleNeedNewRule}
        disabled={insuranceIQRules.length === 0}
        size="small"
      >
        Need New
      </MUIButton>,
    ];
  };

  const renderInsuranceIQFeedbackDialog = () => {
    const missingSelectedRules =
      showInsuranceIQFeedbackValidation && selectedInaccurateRules.length === 0;
    const missingExplanation =
      showInsuranceIQFeedbackValidation &&
      inaccurateRulesExplanation.trim().length === 0;
    const missingNewRuleExplanation =
      showInsuranceIQFeedbackValidation &&
      newRuleExplanation.trim().length === 0;
    const dialogTitle =
      insuranceIQFeedbackDialogType === feedbackTypeNeedNewRule
        ? "Need New"
        : "Some Rules Inaccurate";

    return (
      <DialogFunctional
        open={insuranceIQFeedbackDialogOpen}
        onClose={handleCloseInsuranceIQFeedbackDialog}
        getMaxWidth={() => "sm"}
        renderHeader={() => (
          <>
            <MUITypography variant="h6" color="textPrimary">
              {dialogTitle}
            </MUITypography>
            <MUITypography
              variant="subtitle2"
              color="textSecondary"
              sx={{ flex: 1, pr: 2, textAlign: "left" }}
            >
              {insuranceIQFeedbackConfirmText[insuranceIQFeedbackDialogType]}
            </MUITypography>
          </>
        )}
        renderContent={() => (
          <MUIStack spacing={2} sx={{ pt: 1 }}>
            {insuranceIQFeedbackDialogType ===
              feedbackTypeSomeRulesInaccurate && [
              <MUIAutocomplete
                key="rules"
                multiple
                disableCloseOnSelect
                filterSelectedOptions
                options={insuranceIQRules}
                value={selectedInaccurateRules}
                getOptionLabel={(option) =>
                  getInsuranceIQRuleDisplayName(option)
                }
                isOptionEqualToValue={(option, value) =>
                  option["insuranceRuleTypeTermId"] ===
                  value["insuranceRuleTypeTermId"]
                }
                onChange={(_, value) => setSelectedInaccurateRules(value)}
                renderInput={(params) => (
                  <MUITextField
                    {...params}
                    label="Rules"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    error={missingSelectedRules}
                    helperText={
                      missingSelectedRules ? "Select at least one rule." : ""
                    }
                  />
                )}
              />,
              <MUITextField
                key="explanation"
                label={
                  selectedInaccurateRules.length === 1
                    ? "Why was this rule inaccurate?"
                    : "Why were these rules inaccurate?"
                }
                value={inaccurateRulesExplanation}
                onChange={(e) => setInaccurateRulesExplanation(e.target.value)}
                multiline
                minRows={4}
                fullWidth
                variant="standard"
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 4000, style: { resize: "vertical" } }}
                error={missingExplanation}
                helperText={
                  missingExplanation ? "Explain what was inaccurate." : ""
                }
              />,
            ]}
            {insuranceIQFeedbackDialogType === feedbackTypeNeedNewRule && (
              <MUITextField
                label="Why is a new rule needed?"
                value={newRuleExplanation}
                onChange={(e) => setNewRuleExplanation(e.target.value)}
                multiline
                minRows={4}
                fullWidth
                variant="standard"
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 4000, style: { resize: "vertical" } }}
                error={missingNewRuleExplanation}
                helperText={
                  missingNewRuleExplanation
                    ? "Explain the issue that needs a new rule."
                    : ""
                }
              />
            )}
          </MUIStack>
        )}
        renderActions={() => (
          <MUIDialogActions spacing={2}>
            <MUIButton
              variant="text"
              onClick={handleCloseInsuranceIQFeedbackDialog}
            >
              Cancel
            </MUIButton>
            <MUIButton
              variant="contained"
              onClick={handleSubmitInsuranceIQFeedback}
            >
              Log Feedback
            </MUIButton>
          </MUIDialogActions>
        )}
      />
    );
  };

  const renderInsuranceIQFeedbackConfirmDialog = () => {
    return (
      <DialogConfirm
        open={pendingInsuranceIQFeedback !== null}
        onClose={() => setPendingInsuranceIQFeedback(null)}
        onConfirm={handleConfirmInsuranceIQFeedback}
        header="Confirm Insurance IQ Feedback"
        text={
          insuranceIQFeedbackConfirmText[
            pendingInsuranceIQFeedback?.feedbackType
          ]
        }
        cancelText="Cancel"
        confirmText="Log Feedback"
      />
    );
  };

  const renderEmptyInsuranceIQ = () => {
    return (
      <div style={{ textAlign: "center", paddingTop: 16, paddingBottom: 16 }}>
        <EmptyState
          style={{ textAlign: "center", paddingTop: 16, paddingBottom: 16 }}
          line1="Nothing to show"
          line2="Better luck next time!"
        />
      </div>
    );
  };

  const renderLastRanAtInformation = () => {
    const user =
      mainContext.usersIndexed[insuranceIQRuleResponse?.userprofileId];
    const fullname = User.getDisplayName(user);
    const lastRanAt = insuranceIQRuleResponse?.timestamp
      ? moment
          .utc(insuranceIQRuleResponse.timestamp)
          .local()
          .format("M/D/YYYY [at] h:mma")
      : null;

    if (fullname === null || lastRanAt === null) {
      return null;
    }

    return (
      <MUIStack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 0.5, sm: 2 }}
        sx={{ color: "text.secondary" }}
      >
        <MUIStack direction="row" spacing={0.75} alignItems="center">
          <MUIAccessTimeIcon sx={{ fontSize: 16 }} />
          <MUITypography variant="body2" color="text.secondary">
            Last ran {lastRanAt}
          </MUITypography>
        </MUIStack>
        <MUIStack direction="row" spacing={0.75} alignItems="center">
          <MUIPersonOutlineIcon sx={{ fontSize: 16 }} />
          <MUITypography variant="body2" color="text.secondary">
            {fullname}
          </MUITypography>
        </MUIStack>
      </MUIStack>
    );
  };

  const renderFailedRulesPanel = () => {
    const failedRules = failedOrWarningInsuranceIQRules.filter(
      (rules) => rules.status === "fail",
    );
    const warnedRules = failedOrWarningInsuranceIQRules.filter(
      (rules) => rules.status === "warn",
    );

    return (
      <MUIAlert
        severity={failedRules.length > 0 ? "error" : "warning"}
        icon={false}
      >
        <MUIStack direction="row" spacing={1}>
          {failedRules.length > 0 && (
            <MUITypography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: "error.main" }}
            >
              {`Failed Rules - ${failedRules.length}${warnedRules.length > 0 ? "." : ""}`}
            </MUITypography>
          )}
          {warnedRules.length > 0 && (
            <MUITypography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: "warning.main" }}
            >
              {`Warnings - ${warnedRules.length}`}
            </MUITypography>
          )}
        </MUIStack>
        {failedRules.map((rule) => (
          <MUIStack
            direction="row"
            spacing={0.5}
            key={rule["insuranceRuleTypeTermId"]}
            sx={{ mt: 1.5 }}
          >
            <MUIErrorOutlineOutlinedIcon
              sx={{ fontSize: 20, color: "error.main" }}
            />
            <MUITypography variant="body2" sx={{ fontWeight: 700 }}>
              {getInsuranceIQRuleDisplayName(rule)}
              {rule["message"] ? (
                <MUITypography variant="body2" color="text.secondary">
                  {rule["message"]}
                </MUITypography>
              ) : null}
            </MUITypography>
          </MUIStack>
        ))}
        {warnedRules.map((rule) => (
          <MUIStack
            direction="row"
            spacing={0.5}
            key={rule["insuranceRuleTypeTermId"]}
            sx={{ mt: 1.5 }}
          >
            <MUIWarningAmberIcon sx={{ fontSize: 20, color: "warning.main" }} />
            <MUITypography variant="body2" sx={{ fontWeight: 700 }}>
              {getInsuranceIQRuleDisplayName(rule)}
              {rule["message"] ? (
                <MUITypography variant="body2" color="text.secondary">
                  {rule["message"]}
                </MUITypography>
              ) : null}
            </MUITypography>
          </MUIStack>
        ))}
      </MUIAlert>
    );
  };

  const renderPassedRulesPanel = () => {
    const toggleSuccessfulRulesExpanded = () => {
      setSuccessfulRulesExpanded((expanded) => !expanded);
    };

    const handleSuccessfulRulesKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleSuccessfulRulesExpanded();
      }
    };

    return (
      <MUIAlert
        severity="success"
        icon={false}
        onClick={
          allInsuranceIQRulesPassed ? toggleSuccessfulRulesExpanded : undefined
        }
        onKeyDown={
          allInsuranceIQRulesPassed ? handleSuccessfulRulesKeyDown : undefined
        }
        role={allInsuranceIQRulesPassed ? "button" : undefined}
        tabIndex={allInsuranceIQRulesPassed ? 0 : undefined}
        aria-expanded={
          allInsuranceIQRulesPassed ? successfulRulesExpanded : undefined
        }
        sx={
          allInsuranceIQRulesPassed
            ? {
                cursor: "pointer",
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }
            : undefined
        }
      >
        <MUIStack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <MUITypography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: "success.main" }}
          >
            {`Successful Rules - ${passedInsuranceIQRules.length}`}
          </MUITypography>
          {allInsuranceIQRulesPassed ? (
            <MUIExpandMoreIcon
              fontSize="small"
              sx={{
                flexShrink: 0,
                transform: successfulRulesExpanded
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 150ms ease",
              }}
            />
          ) : null}
        </MUIStack>
        <MUICollapse
          in={!allInsuranceIQRulesPassed || successfulRulesExpanded}
          timeout="auto"
          unmountOnExit={allInsuranceIQRulesPassed}
        >
          {passedInsuranceIQRules.map((rule) => (
            <MUIStack
              direction="row"
              spacing={0.5}
              key={rule["insuranceRuleTypeTermId"]}
              sx={{ mt: 1.5 }}
            >
              <MUICheckCircleOutlineOutlinedIcon
                sx={{ fontSize: 20, color: "success.main" }}
              />
              <MUITypography variant="body2" sx={{ fontWeight: 700 }}>
                {getInsuranceIQRuleDisplayName(rule)}
              </MUITypography>
            </MUIStack>
          ))}
        </MUICollapse>
      </MUIAlert>
    );
  };

  const renderFooter = () => {
    return (
      <MUIStack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        {renderLastRanAtInformation()}
        <MUIStack sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}>
          {renderInsuranceIQRunButton()}
        </MUIStack>
      </MUIStack>
    );
  };

  /**
   * Determines which view to render based on data within InsuranceIQRules response.
   * @returns {JSX.Element} read-only view
   */
  const renderContent = () => {
    if (insuranceIQRules.length === 0) {
      return (
        <MUIStack spacing={2}>
          {renderEmptyInsuranceIQ()}
          <MUIStack direction="row" justifyContent="flex-end">
            {renderInsuranceIQRunButton()}
          </MUIStack>
        </MUIStack>
      );
    }

    return (
      <MUIStack spacing={2}>
        <MUIStack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          {failedOrWarningInsuranceIQRules.length > 0 ? (
            <MUIStack
              sx={{
                flex: passedInsuranceIQRules.length > 0 ? 7 : 1,
                alignSelf: "stretch",
                "& > .MuiAlert-root": {
                  height: "100%",
                  boxSizing: "border-box",
                },
              }}
            >
              {renderFailedRulesPanel()}
            </MUIStack>
          ) : null}
          {passedInsuranceIQRules.length > 0 ? (
            <MUIStack
              sx={{
                flex: failedOrWarningInsuranceIQRules.length > 0 ? 5 : 1,
                alignSelf: "stretch",
                "& > .MuiAlert-root": {
                  height: "100%",
                  boxSizing: "border-box",
                },
              }}
            >
              {renderPassedRulesPanel()}
            </MUIStack>
          ) : null}
        </MUIStack>
        {renderFooter()}
      </MUIStack>
    );
  };

  /** Render the card. */
  return (
    <>
      <Card
        renderContent={renderContent}
        getActions={getActions}
        getTitle={() => title}
        getIcon={() => icon}
      />
      {renderInsuranceIQFeedbackDialog()}
      {renderInsuranceIQFeedbackConfirmDialog()}
    </>
  );
}

CardChartInsuranceIQ.meta = { className: className, title: title, icon: icon };
